<script lang="ts">
    import Button from "@/lib/components/ui/button/button.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { Checkbox } from "@/lib/components/ui/checkbox";
    import SocialButtons from "./SocialButtons.svelte";
    import { getRequest } from "@/Util";

    export let oauth: number | null = null
    export let redirect: number | null = null
    export let onForgot: () => void
    export let onSignup: () => void

    let username = ''
    let password = ''
    let remember = false
    let showPassword = false
    let loading = false
    let error = ''

    async function submit() {
        if (!username || !password) {
            error = 'Please enter your username and password.'
            return
        }
        error = ''
        loading = true
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            const body: Record<string, unknown> = { username, password, timezone, oauth, redirect }
            // Only send the key when the box is ticked. The server reads it with
            // boolean(), so `remember: 0` would be handled correctly anyway — this
            // just keeps the request honest about what the user asked for.
            if (remember) body.remember = 1

            const res = await fetch('/auth/login', {
                ...getRequest(),
                method: 'POST',
                body: JSON.stringify(body),
            })
            // A CSRF/session failure answers with HTML, not JSON.
            const data = await res.json().catch(() => ({}))
            if (res.ok && data.login_success) {
                window.location.href = data.redirect
            } else if (res.status === 429) {
                error = 'Too many sign-in attempts. Please wait a minute and try again.'
            } else {
                error = data.error ?? 'Login failed. Please try again.'
            }
        } catch {
            error = 'A network error occurred. Please try again.'
        } finally {
            loading = false
        }
    }
</script>

<div class="space-y-6">
    <div class="space-y-2 text-center">
        <h1 class="text-2xl font-semibold tracking-tight">Welcome back</h1>
        <p class="text-sm text-muted-foreground">Sign in to your Xert account</p>
    </div>

    <SocialButtons mode="login" />

    <form on:submit|preventDefault={submit} class="space-y-4">
        {#if error}
            <div class="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                {error}
            </div>
        {/if}

        <div class="space-y-1.5">
            <Label for="login-username">Username or Email</Label>
            <Input
                id="login-username"
                type="text"
                autocomplete="username"
                placeholder="you@example.com"
                bind:value={username}
                disabled={loading}
                required
            />
        </div>

        <div class="space-y-1.5">
            <div class="flex items-center justify-between">
                <Label for="login-password">Password</Label>
                <button
                    type="button"
                    class="text-xs text-xerteal-600 hover:underline"
                    on:click={onForgot}
                >
                    Forgot password?
                </button>
            </div>
            <div class="relative">
                <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autocomplete="current-password"
                    placeholder="••••••••"
                    bind:value={password}
                    disabled={loading}
                    required
                    class="pr-10"
                />
                <button
                    type="button"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    on:click={() => (showPassword = !showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                    {#if showPassword}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21"/></svg>
                    {:else}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                    {/if}
                </button>
            </div>
        </div>

        <div class="flex items-center gap-2">
            <Checkbox id="login-remember" bind:checked={remember} />
            <Label for="login-remember" class="font-normal cursor-pointer">Remember me</Label>
        </div>

        <Button type="submit" class="w-full" disabled={loading}>
            {#if loading}
                <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {/if}
            Sign in
        </Button>
    </form>

    <p class="text-center text-sm text-muted-foreground">
        New to Xert?
        <button type="button" class="font-medium text-xerteal-600 hover:underline" on:click={onSignup}>
            Start your free trial
        </button>
    </p>
</div>
